/**
 * /docs/using-apps — entry-point doc for non-builders. Someone who has
 * been pointed at the portal and just sees the Apps page lands here.
 */

import { Link } from 'react-router-dom';
import {
  IconBolt,
  IconExternal,
  IconLock,
  IconRocket,
  IconShield,
  IconUsers,
} from '../../components/Icons.js';
import { ButtonLink, PageHeader } from '../../components/ui.js';
import { DocsTabs } from '../../components/DocsTabs.js';
import { Callout, DocSection } from './_shared.js';

export function DocsUsingApps() {
  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Docs · For end users"
        title="Using Kin apps"
        description="You don't have to build anything. Most people just use what their teammates built."
      />

      <DocsTabs />

      <DocSection title="What is Kin">
        <p>
          Kin is Mytra&apos;s home for small internal tools &mdash; a lunch sign-up tracker, a wifi
          lookup, a weekly Slack digest, a calculator, a form. Anyone at Mytra can ask an AI to
          build one. Kin hosts it, signs you in, and handles sharing.
        </p>
        <p>
          The Apps page is the front door. Every app shared with you (or with all of Mytra) shows up
          there. Click a card &mdash; the whole card &mdash; and the live app opens in a new tab.
        </p>
      </DocSection>

      <DocSection title="The Apps page, decoded">
        <ul className="space-y-3">
          <li className="flex gap-3">
            <span className="mt-1 h-8 w-8 rounded-lg bg-brand-soft text-brand flex-none flex items-center justify-center">
              <IconExternal size={14} />
            </span>
            <div>
              <p className="font-medium text-ink">Click anywhere on the card to open the app.</p>
              <p className="text-muted">
                The URL is <span className="font-mono text-xs">https://{`{slug}`}.mkin.app</span>.
                The <em>Open ↗</em> hint in the corner brightens on hover to confirm.
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="mt-1 h-8 w-8 rounded-lg bg-brand-soft text-brand flex-none flex items-center justify-center">
              <IconUsers size={14} />
            </span>
            <div>
              <p className="font-medium text-ink">The avatars at the bottom are the managers.</p>
              <p className="text-muted">
                Wrong behavior, missing feature, can&apos;t get in &mdash; that&apos;s who to Slack.
                They can change anything about the app.
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="mt-1 h-8 w-8 rounded-lg bg-brand-soft text-brand flex-none flex items-center justify-center">
              <IconLock size={14} />
            </span>
            <div>
              <p className="font-medium text-ink">
                &ldquo;Mytra-wide&rdquo; means anyone at Mytra can see it.
              </p>
              <p className="text-muted">
                No tag = private; you were added specifically. Nothing on{' '}
                <span className="font-mono text-xs">mkin.app</span> is reachable from outside Mytra
                either way.
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="mt-1 h-8 w-8 rounded-lg bg-brand-soft text-brand flex-none flex items-center justify-center">
              <IconShield size={14} />
            </span>
            <div>
              <p className="font-medium text-ink">Star a card to pin it under Favorites.</p>
              <p className="text-muted">
                The star at the top-right of each card flips on hover. The filter at the top of the
                page switches between <strong>All</strong>, <strong>Mine</strong>, and{' '}
                <strong>Favorites</strong>; the search box filters the active set.
              </p>
            </div>
          </li>
        </ul>
      </DocSection>

      <DocSection title="Opening an app">
        <p>
          Your Mytra sign-in carries over &mdash; the app already knows it&apos;s you. If you hit a
          sign-in screen, your session expired; sign in once and you&apos;re good for another month.
        </p>
        <Callout title="If you get &ldquo;You don't have access&rdquo;">
          <p>
            The app is private and you&apos;re not on the list. Ask a manager on the card; for them
            it&apos;s a two-second job in this portal.
          </p>
        </Callout>
      </DocSection>

      <DocSection title="Using an app well">
        <ul className="list-disc list-inside space-y-1.5 pl-1">
          <li>
            <strong>Your identity is real.</strong> Kin apps see your Mytra email, name, and avatar.
            If an app asks who you are, that&apos;s a bug.
          </li>
          <li>
            <strong>Don&apos;t paste secrets into form fields unless that&apos;s the point.</strong>{' '}
            If something feels off, ping the owner before pasting an API token or a customer record.
          </li>
          <li>
            <strong>Refresh for new data.</strong> Most Kin apps render on page load, not live.
          </li>
          <li>
            <strong>Mobile mostly works.</strong> If a specific app is awkward on a phone, tell the
            owner &mdash; it&apos;s a 30-second prompt to fix.
          </li>
        </ul>
      </DocSection>

      <DocSection title="Asking for changes">
        <p>The unblocking move 90% of the time:</p>
        <ol className="list-decimal list-inside space-y-1 pl-1">
          <li>Hover the managers facepile at the bottom of the card to see names.</li>
          <li>
            Slack one of them:{' '}
            <em>
              &ldquo;hey, the lunch app &mdash; could you add a column for dietary
              preference?&rdquo;
            </em>
          </li>
          <li>They prompt their AI, redeploy, you refresh. Under five minutes.</li>
        </ol>
        <p>Changes that would be a ticket on any other system are a Slack message here.</p>
      </DocSection>

      <DocSection title="Apps that aren't on your list yet">
        <p>Mytra-wide apps appear automatically. If you don&apos;t see one a teammate mentioned:</p>
        <ul className="list-disc list-inside space-y-1 pl-1">
          <li>
            <strong>Private</strong> &mdash; ask a manager to add your email.
          </li>
          <li>
            <strong>Unpublished</strong> &mdash; only preview exists; a manager can send the preview
            URL directly.
          </li>
        </ul>
        <p>
          On the{' '}
          <Link className="underline" to="/">
            Apps
          </Link>{' '}
          page, switch the filter to <strong>Mine</strong> to see just the apps you manage.
        </p>
      </DocSection>

      <DocSection title="Calling apps from scripts or AI clients">
        <p>
          If a manager has opened paths for programmatic access, you can mint a bearer token for
          yourself and call the app from Claude Code, a script, or a webhook. Open the app from the
          Apps page, scroll to <strong>Programmatic access</strong>, generate a token, copy it. It
          shows once &mdash; save it then.
        </p>
        <p>
          No <strong>Programmatic access</strong> section on the app&apos;s page means the manager
          hasn&apos;t opened any paths. Ask them to add one (a short prompt to their AI).
        </p>
      </DocSection>

      <DocSection title="Want to build one?">
        <p>You don&apos;t write code. Two paths in:</p>
        <ul className="list-disc list-inside space-y-1 pl-1">
          <li>
            <strong>In the browser</strong> &mdash; click <strong>Create app</strong> on the Apps
            page, land on a preview URL with a chat bubble, tell it what you want. Zero install.
          </li>
          <li>
            <strong>Claude Code</strong> &mdash; one-command plugin install, then{' '}
            <em>&ldquo;Make me a Kin app to ...&rdquo;</em> in any session. For power users who
            already live in a terminal.
          </li>
        </ul>
        <p>Either way, a simple tool is ten minutes from prompt to URL.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <ButtonLink to="/apps/new" variant="primary" size="sm">
            <IconBolt size={14} />
            Create your first app
          </ButtonLink>
          <ButtonLink to="/docs/getting-started" variant="secondary" size="sm">
            <IconRocket size={14} />
            Pick a path
          </ButtonLink>
          <ButtonLink to="/docs/examples" variant="ghost" size="sm">
            See examples
          </ButtonLink>
        </div>
      </DocSection>
    </div>
  );
}
